import {
  Scale,
  ScrollText,
  Globe,
  FileText,
  Building,
  AlertTriangle,
  Shield,
  Eye,
  Heart,
  Users,
  Home,
  Lock,
  Flame,
  Landmark
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface Subject {
  id: string;
  name: string;
  shortName: string;
  icon: LucideIcon;
  description: string;
  color: string; // hex color
}

export const subjects: Subject[] = [
  {
    id: 'agency',
    name: 'Agency',
    shortName: 'Agency',
    icon: Users,
    description: 'Principal-agent relationships, authority, and liability',
    color: '#8844aa',
  },
  {
    id: 'civil-procedure',
    name: 'Civil Procedure',
    shortName: 'Civ Pro',
    icon: Scale,
    description: 'Federal rules, jurisdiction, pleading, and discovery',
    color: '#4488aa',
  },
  {
    id: 'con-law',
    name: 'Con Law',
    shortName: 'Con Law',
    icon: ScrollText,
    description: 'Constitutional principles, amendments, and judicial review',
    color: '#aa4444',
  },
  {
    id: 'conflicts-of-law',
    name: 'Conflicts Of Law',
    shortName: 'Conflicts',
    icon: Globe,
    description: 'Choice of law, jurisdiction, and recognition of judgments',
    color: '#44aa88',
  },
  {
    id: 'contracts',
    name: 'Contracts',
    shortName: 'Contracts',
    icon: FileText,
    description: 'Formation, performance, breach, and remedies',
    color: '#aaaa44',
  },
  {
    id: 'corporations',
    name: 'Corporations',
    shortName: 'Corps',
    icon: Building,
    description: 'Corporate formation, governance, and shareholder rights',
    color: '#44aa44',
  },
  {
    id: 'criminal-law',
    name: 'Criminal Law',
    shortName: 'Crim Law',
    icon: AlertTriangle,
    description: 'Crimes, defenses, and inchoate offenses',
    color: '#aa8844',
  },
  {
    id: 'criminal-procedure',
    name: 'Criminal Procedure',
    shortName: 'Crim Pro',
    icon: Shield,
    description: 'Fourth, Fifth, and Sixth Amendment protections',
    color: '#8888aa',
  },
  {
    id: 'evidence',
    name: 'Evidence',
    shortName: 'Evidence',
    icon: Eye,
    description: 'Relevance, hearsay, privileges, and impeachment',
    color: '#aa4488',
  },
  {
    id: 'family-law',
    name: 'Family Law',
    shortName: 'Family',
    icon: Heart,
    description: 'Marriage, divorce, custody, and support',
    color: '#ff8888',
  },
  {
    id: 'partnerships',
    name: 'Partnerships',
    shortName: 'Partners',
    icon: Users,
    description: 'Partnership formation, duties, and liability',
    color: '#88ff88',
  },
  {
    id: 'real-property',
    name: 'Real Property',
    shortName: 'Property',
    icon: Home,
    description: 'Estates, conveyancing, mortgages, and land use',
    color: '#88aaff',
  },
  {
    id: 'secured-transactions',
    name: 'Secured Transactions',
    shortName: 'Secured',
    icon: Lock,
    description: 'Article 9, attachment, perfection, and priority',
    color: '#ffaa88',
  },
  {
    id: 'torts',
    name: 'Torts',
    shortName: 'Torts',
    icon: Flame,
    description: 'Negligence, strict liability, and intentional torts',
    color: '#ff4444',
  },
  {
    id: 'trusts-and-estates',
    name: 'Trusts And Estates',
    shortName: 'Trusts',
    icon: Landmark,
    description: 'Wills, trusts, and estate administration',
    color: '#ffff88',
  },
];

/**
 * Get subject by name (case-insensitive match)
 */
export function getSubjectByName(name: string): Subject | undefined {
  const normalizedName = name.toLowerCase();
  return subjects.find(
    s => s.name.toLowerCase() === normalizedName ||
         s.shortName.toLowerCase() === normalizedName
  );
}

/**
 * Get subject by id
 */
export function getSubjectById(id: string): Subject | undefined {
  return subjects.find(s => s.id === id);
}

/**
 * Get subject color as hex
 */
export function getSubjectColor(subjectName: string): string {
  const subject = getSubjectByName(subjectName);
  return subject?.color || '#4488ff';
}
